import React, { useState } from 'react';
import { MdEmojiPeople, MdSearch } from 'react-icons/md';
import { useDebounce } from 'use-debounce';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { useMultiTierGuestListSearch } from 'legacy/search/hooks';
import Loader from 'legacy/common/components/layout/Loader';
import GridUser from './components/GridUser';
import {
  useFriendRequestCommandsForGuestList,
  useGetMe
} from 'legacy/user/hooks';
import { showModal } from 'legacy/actions/modalActions';
import { EventGuestsCategories } from 'legacy/search/types';
import TabButton from './components/TabButton';

import styles from './GuestModal.module.scss';

type Props = {
  onClose: () => void;
  eventId: string;
};

const SCROLL_OFFSET = 20;

const GuestGridList = ({ eventId, onClose }: Props) => {
  const [search, setSearch] = useState('');
  const { data: user } = useGetMe();
  const dispatch = useDispatch();
  const router = useRouter();
  const [debouncedSearch] = useDebounce(search, 200);
  const {
    data: guests,
    isLoading: isLoadingGuestList,
    fetchNextPage,
    isFetchingNextPage
  } = useMultiTierGuestListSearch(eventId, debouncedSearch);
  const [selectedCategory, setSelectedCategory] = useState(
    EventGuestsCategories.All
  );

  const guestsData =
    guests?.pages?.reduce(
      (guestsArray, guestsPage) => [
        ...guestsArray,
        ...guestsPage[selectedCategory].users
      ],
      []
    ) || [];

  const friendRequestCommands = useFriendRequestCommandsForGuestList({
    eventId,
    searchTerm: debouncedSearch,
    multitierQuery: true
  });

  const onGuestClick = (guest) => {
    if (guest.id !== user._id) {
      dispatch(
        showModal('QUICKVIEW_PROFILE', {
          userId: guest.id
        })
      );
    } else {
      router.push(`/profile/${user._id}`);
      onClose();
    }
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  // Note: Old code
  // Allows fetching the next page of results after scrolling to the bottom of the modal
  // https://medium.com/fredwong-it/react-detect-scroll-to-bottom-event-7c18350c4ef6
  const handleScroll = ({ target }) => {
    if (isLoadingGuestList || isFetchingNextPage) {
      return;
    }

    const currentPageNumber = guests.pages.length - 1;
    const currentPage = guests.pages[currentPageNumber];

    if (
      target.scrollHeight - target.scrollTop <=
        target.clientHeight + SCROLL_OFFSET &&
      currentPage.all.total !== 0
    ) {
      fetchNextPage({ pageParam: currentPageNumber + 1 });
    }
  };

  return (
    <Modal
      visible
      onClose={onClose}
      type={ModalType.SMALL}
      title="Guest List"
      icon={<MdEmojiPeople size={24} />}
    >
      <Loader isOverlay isLoading={isLoadingGuestList || isFetchingNextPage}>
        <div className="px-5">
          <div className="flex text-center justify-between border-b text-base border-gray-100 text-gray-500">
            <TabButton
              onClick={() => setSelectedCategory(EventGuestsCategories.All)}
              active={selectedCategory === EventGuestsCategories.All}
            >
              All
            </TabButton>
            <TabButton
              onClick={() => setSelectedCategory(EventGuestsCategories.Friends)}
              active={selectedCategory === EventGuestsCategories.Friends}
            >
              Friends
            </TabButton>
            <TabButton
              onClick={() =>
                setSelectedCategory(EventGuestsCategories.Organizers)
              }
              active={selectedCategory === EventGuestsCategories.Organizers}
            >
              Hosts
            </TabButton>
          </div>
        </div>
        <div className="h-full pb-32 overflow-y-auto" onScroll={handleScroll}>
          <div className="mt-6 ml-4 mr-4 relative h-12 border-1 border-solid border-gray-300 rounded-3xl flex align-center">
            <MdSearch className="absolute left-2.5 text-gray-400" size={24} />
            <input
              type="text"
              className="w-full h-full px-10 border-0 text-base font-normal text-gray-600 placeholder:text-gray-400 rounded-3xl"
              placeholder="Search members"
              onChange={handleSearch}
              value={search}
            />
          </div>
          <div className={styles.gridListContainer}>
            {guestsData.map((guest) => (
              <GridUser
                key={guest.id}
                guest={guest}
                friendRequestCommands={friendRequestCommands}
                onGuestClick={onGuestClick}
              />
            ))}
          </div>
        </div>
      </Loader>
    </Modal>
  );
};

export default GuestGridList;
